import { useRouterState } from '@tanstack/react-router';
import { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

// Matches the static <title> in index.html, which browsers show before the bundle boots.
export const BRAND_TITLE = 'Houzy Partner Cockpit';

export function formatDocumentTitle(pageTitle: string | undefined): string {
    return pageTitle ? `${pageTitle} | ${BRAND_TITLE}` : BRAND_TITLE;
}

// Deepest match wins, so /settings/company titles itself "Firma" rather than inheriting
// the layout's "Einstellungen", while the reimbursement steps do inherit from theirs.
export function selectPageTitleKey(matches: ReadonlyArray<{ staticData: { titleKey?: string } }>): string | undefined {
    for (let index = matches.length - 1; index >= 0; index--) {
        const key = matches[index].staticData.titleKey;
        if (key) {
            return key;
        }
    }
    return undefined;
}

let overrideTitle: string | undefined;
const overrideListeners = new Set<() => void>();

function setOverrideTitle(title: string | undefined): void {
    overrideTitle = title;
    for (const listener of overrideListeners) {
        listener();
    }
}

function subscribeToOverride(listener: () => void): () => void {
    overrideListeners.add(listener);
    return () => {
        overrideListeners.delete(listener);
    };
}

function readOverride(): string | undefined {
    return overrideTitle;
}

// For a title a route can't know statically, e.g. built from loaded data. One caller at a
// time: the override is a single slot, so two mounted callers would clobber each other.
export function useDocumentTitle(title: string | undefined): void {
    useEffect((): (() => void) => {
        setOverrideTitle(title);
        return () => setOverrideTitle(undefined);
    }, [title]);
}

// Called once, from RootLayout. Resolving the title in React rather than in a route's
// `head()` is deliberate: `head()` runs while the route loads, but translations arrive
// from the backend over HTTP after first paint, so a title built there would be stuck at
// the raw key. Resolving it here re-renders when i18next loads or changes language.
export function useSyncDocumentTitle(): void {
    const { t } = useTranslation();
    const titleKey = useRouterState({ select: (state) => selectPageTitleKey(state.matches) });
    const override = useSyncExternalStore(subscribeToOverride, readOverride);

    const pageTitle = override ?? (titleKey ? t(titleKey) : undefined);

    useEffect(() => {
        document.title = formatDocumentTitle(pageTitle);
    }, [pageTitle]);
}
